import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import Select from "~/components/formElements/Select";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import useGenderSelectOptions from "~/hooks/form/formOptionsData/useGenderSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { createStaffMember } from "~/modules/humanResources/api/staffMember/staffMemberApiDispatchers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type CreateStaffMemberFormData = {
	firstName: string;
	lastName: string;
	genderId: string;
}

type CreateStaffMemberFormProps = {
	onSuccess: () => void;
	onCancel: () => void;
};

const CreateStaffMemberForm: React.FunctionComponent<CreateStaffMemberFormProps> = ({
	onSuccess,
	onCancel,
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const genderSelectionOptions = useGenderSelectOptions();
	const schema = useMemo(() => {
		return yup.object({
			firstName: yup.string().required(),
			lastName: yup.string().required(),
			genderId: yup.string().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			firstName: "",
			lastName: "",
			genderId: "",
		};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<CreateStaffMemberFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateStaffMemberFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: CreateStaffMemberFormData) => {
		try {
			setIsBusy(true);
			await createStaffMember(data);

			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			console.log(apiError);
			setServerErrorMessage("Ein unerwarteter Fehler ist aufgetreten.");
		}
	}, [onSuccess]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<Headline type="h4"
						  color="muted">Neue/r Mitarbeiter/in</Headline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Mitarbeiter/in konnte nicht angelegt werden">{serverErrorMessage}</SidebarErrorOverlay>}
				<div className="grid grid-cols-2 gap-4">
					<Input control={control}
						   name="firstName"
						   label="Vorname"
						   placeholder="Vorname"
					/>
					<Input control={control}
						   name="lastName"
						   label="Nachname"
						   placeholder="Nachname"
					/>
					<Select optionsData={genderSelectionOptions}
							name="genderId"
							control={control}
							label="Geschlecht" />
				</div>
			</SidebarContent>

			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateStaffMemberForm;
